import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import api from '../../services/api';
import { ChunkedUploader, shouldUseChunkedUpload } from '../../utils/chunkedUploader';
import ConfirmModal from './ConfirmModal';
import './FileAttachments.css';

/**
 * File Attachments component for uploading and managing files
 */
const FileAttachments = ({
  entityType,
  entityId,
  attachments = [],
  onUploadComplete,
  onDeleteComplete,
  canUpload = true,
  canDelete = true,
  maxFiles = 10,
  compact = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Delete',
    variant: 'danger',
    onConfirm: null,
    isLoading: false
  });

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    if (!canUpload) return;

    if (rejectedFiles && rejectedFiles.length > 0) {
      rejectedFiles.forEach(({ errors }) => {
        const errorMessages = errors.map(e => {
          if (e.code === 'too-many-files') return `Too many files (max ${maxFiles})`;
          return e.message;
        });
        toast.error(errorMessages.join('. '));
      });
    }

    if (acceptedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    const uploadedFiles = [];
    const failedNames = [];

    for (let i = 0; i < acceptedFiles.length; i++) {
      const file = acceptedFiles[i];
      const fileIndex = i;

      if (shouldUseChunkedUpload(file.size)) {
        await new Promise((resolve) => {
          const uploader = new ChunkedUploader(file, {
            endpoints: {
              initiate: '/attachments/upload/initiate',
              complete: '/attachments/upload/complete',
              abort: '/attachments/upload/abort',
            },
            extraInitiateFields: { entity_type: entityType, entity_id: entityId },
            extraCompleteFields: { entity_type: entityType, entity_id: entityId },
            onProgress: ({ percentage }) => {
              const baseProgress = Math.round((fileIndex / acceptedFiles.length) * 100);
              const fileContrib = Math.round((percentage / 100) * (100 / acceptedFiles.length));
              setUploadProgress(baseProgress + fileContrib);
            },
            onComplete: (data) => {
              uploadedFiles.push(data.attachment);
              resolve();
            },
            onError: () => {
              failedNames.push(file.name);
              resolve();
            },
          });
          uploader.start();
        });
      } else {
        try {
          const formData = new FormData();
          formData.append('file', file);
          const response = await api.post(
            `/attachments/upload?entity_type=${entityType}&entity_id=${entityId}`,
            formData,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
              onUploadProgress: (progressEvent) => {
                const baseProgress = Math.round((fileIndex / acceptedFiles.length) * 100);
                const fileContrib = Math.round(
                  (progressEvent.loaded / progressEvent.total) * (100 / acceptedFiles.length)
                );
                setUploadProgress(baseProgress + fileContrib);
              },
            }
          );
          uploadedFiles.push(response.data.attachment);
        } catch (error) {
          failedNames.push(file.name);
        }
      }
    }

    if (uploadedFiles.length > 0) {
      toast.success(`${uploadedFiles.length} file(s) uploaded successfully`);
      if (onUploadComplete) onUploadComplete(uploadedFiles);
    }
    if (failedNames.length > 0) {
      toast.error(`Failed to upload: ${failedNames.join(', ')}`);
    }

    setUploading(false);
    setUploadProgress(0);
  }, [entityType, entityId, canUpload, onUploadComplete, maxFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: !canUpload || uploading,
    maxFiles,
  });

  const handleDelete = (attachmentId, filename) => {
    if (!canDelete) return;
    setConfirmModal({
      isOpen: true,
      title: 'Delete File',
      message: `Are you sure you want to delete "${filename}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await api.delete(`/attachments/${attachmentId}`);
          toast.success('File deleted');
          if (onDeleteComplete) {
            onDeleteComplete(attachmentId);
          }
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to delete file');
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };

  const handleDownload = async (attachment) => {
    try {
      // direct_url is a presigned S3 URL with ResponseContentDisposition set to
      // "attachment; filename=original_name.ext" (RFC 5987), so the browser always
      // saves with the correct filename. Never stream through the backend — it
      // loads the entire file into memory and breaks for large files.
      let url = attachment.direct_url;

      if (!url) {
        // No cached URL — fetch a fresh presigned URL from the API
        const response = await api.get(`/attachments/${attachment.id}/url`, {
          params: { download: true }
        });
        url = response.data.download_url;
      }

      if (!url) {
        toast.error('Failed to get download URL');
        return;
      }

      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType.startsWith('image/')) return '🖼️';
    if (contentType.startsWith('video/')) return '🎬';
    if (contentType === 'application/pdf') return '📄';
    if (contentType.includes('word')) return '📝';
    if (contentType.includes('excel') || contentType.includes('spreadsheet')) return '📊';
    if (contentType.includes('powerpoint') || contentType.includes('presentation')) return '📽️';
    if (contentType.includes('zip') || contentType.includes('rar') || contentType.includes('7z')) return '📦';
    if (contentType.startsWith('text/')) return '📃';
    return '📎';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className={`file-attachments ${compact ? 'compact' : ''}`}>
      {/* Upload area */}
      {canUpload && (
        <div
          {...getRootProps()}
          className={`dropzone ${isDragActive ? 'active' : ''} ${uploading ? 'uploading' : ''} ${compact ? 'compact' : ''}`}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <div className="upload-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <span className="progress-text">Uploading... {uploadProgress}%</span>
            </div>
          ) : isDragActive ? (
            <p>Drop files here...</p>
          ) : (
            <p>
              <span className="dropzone-icon">📁</span>
              Drag & drop files here, or click to select
            </p>
          )}
        </div>
      )}

      {/* Attachments list */}
      {attachments.length > 0 && (
        <div className="attachments-list">
          {attachments.map((attachment) => (
            <AttachmentItem
              key={attachment.id}
              attachment={attachment}
              onDownload={handleDownload}
              onDelete={handleDelete}
              canDelete={canDelete}
              getFileIcon={getFileIcon}
              formatFileSize={formatFileSize}
            />
          ))}
        </div>
      )}

      {attachments.length === 0 && !canUpload && (
        <div className="no-attachments">No attachments</div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        variant={confirmModal.variant}
        isLoading={confirmModal.isLoading}
      />
    </div>
  );
};

/**
 * Lightbox preview for images and videos with zoom/pan
 */
const Lightbox = ({ attachment, isImage, isVideo, isPdf, onClose }) => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const contentRef = useRef(null);

  const mediaUrl = attachment.direct_url || attachment.download_url
    || `/api/v1/attachments/${attachment.id}/download?preview=true`;

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Prevent body scroll while lightbox is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 5));
  };

  const handleZoomOut = () => {
    setZoom(prev => {
      const next = Math.max(prev - 0.25, 0.25);
      if (next <= 1) setPan({ x: 0, y: 0 });
      return next;
    });
  };

  const handleZoomReset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleWheel = (e) => {
    if (!isImage) return;
    e.preventDefault();
    if (e.deltaY < 0) {
      setZoom(prev => Math.min(prev + 0.15, 5));
    } else {
      setZoom(prev => {
        const next = Math.max(prev - 0.15, 0.25);
        if (next <= 1) setPan({ x: 0, y: 0 });
        return next;
      });
    }
  };

  const handleMouseDown = (e) => {
    if (!isImage || zoom <= 1) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="lightbox-overlay"
      onClick={handleOverlayClick}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Header */}
      <div className="lightbox-header">
        <span className="lightbox-filename">{attachment.original_filename}</span>
        <div className="lightbox-controls">
          {isImage && (
            <>
              <button className="lightbox-btn" onClick={handleZoomOut} title="Zoom out">-</button>
              <span className="lightbox-zoom-level">{Math.round(zoom * 100)}%</span>
              <button className="lightbox-btn" onClick={handleZoomIn} title="Zoom in">+</button>
              <button className="lightbox-btn" onClick={handleZoomReset} title="Fit to screen">Fit</button>
              <span className="lightbox-divider" />
            </>
          )}
          <button className="lightbox-btn lightbox-close" onClick={onClose} title="Close">✕</button>
        </div>
      </div>

      {/* Content */}
      <div
        className="lightbox-content"
        ref={contentRef}
        onWheel={handleWheel}
      >
        {isImage && (
          <img
            src={mediaUrl}
            alt={attachment.original_filename}
            className="lightbox-image"
            style={{
              transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
              cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
            }}
            onMouseDown={handleMouseDown}
            draggable={false}
          />
        )}
        {isVideo && (
          <video
            src={mediaUrl}
            controls
            autoPlay
            className="lightbox-video"
          >
            Your browser does not support the video tag.
          </video>
        )}
        {isPdf && (
          <iframe
            src={mediaUrl}
            title={attachment.original_filename}
            className="lightbox-pdf"
          />
        )}
      </div>
    </div>
  );
};

/**
 * Individual attachment item
 */
const AttachmentItem = ({
  attachment,
  onDownload,
  onDelete,
  canDelete,
  getFileIcon,
  formatFileSize
}) => {
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(false);

  const isImage = attachment.is_image || attachment.content_type?.startsWith('image/');
  const isVideo = attachment.content_type?.startsWith('video/');
  const isPdf = attachment.is_pdf || attachment.content_type === 'application/pdf';

  // Office document types that can be previewed
  const isOfficeDoc = attachment.is_office_doc || (attachment.content_type && (
    attachment.content_type.includes('word') ||
    attachment.content_type.includes('excel') ||
    attachment.content_type.includes('spreadsheet') ||
    attachment.content_type.includes('powerpoint') ||
    attachment.content_type.includes('presentation')
  ));

  const canPreview = isImage || isVideo || isPdf || isOfficeDoc;

  const handlePreview = () => {
    if (isOfficeDoc && attachment.viewer_url) {
      window.open(attachment.viewer_url, '_blank');
    } else if (isImage || isVideo || isPdf) {
      setShowPreview(true);
    } else if (attachment.direct_url || attachment.download_url) {
      window.open(attachment.direct_url || attachment.download_url, '_blank');
    }
  };

  const handleClick = () => {
    if (canPreview) {
      handlePreview();
    } else {
      onDownload(attachment);
    }
  };

  return (
    <div className="attachment-item">
      <div className="attachment-icon">
        {getFileIcon(attachment.content_type)}
      </div>

      <div className="attachment-info">
        <span
          className="attachment-name"
          onClick={handleClick}
          title={`${canPreview ? 'Click to view' : 'Click to download'} ${attachment.original_filename}`}
          style={{ cursor: 'pointer', color: canPreview ? '#0066cc' : 'inherit', textDecoration: canPreview ? 'underline' : 'none' }}
        >
          {attachment.original_filename}
        </span>
        <div className="attachment-meta">
          <span className="attachment-size">
            {attachment.file_size_formatted || formatFileSize(attachment.file_size)}
          </span>
          {attachment.uploaded_by_name && (
            <span className="attachment-uploader">
              by {attachment.uploaded_by_name}
            </span>
          )}
          <span className="attachment-date">
            {format(new Date(attachment.created_at), 'MMM d, yyyy')}
          </span>
        </div>
      </div>

      <div className="attachment-actions">
        {canPreview && (
          <button
            className="btn btn-sm btn-ghost"
            onClick={handlePreview}
            title="Preview"
          >
            👁️
          </button>
        )}
        <button
          className="btn btn-sm btn-ghost"
          onClick={() => onDownload(attachment)}
          title="Download"
        >
          ⬇️
        </button>
        {canDelete && (
          <button
            className="btn btn-sm btn-ghost btn-danger"
            onClick={() => onDelete(attachment.id, attachment.original_filename)}
            title="Delete"
          >
            🗑️
          </button>
        )}
      </div>

      {/* Lightbox preview */}
      {showPreview && (
        <Lightbox
          attachment={attachment}
          isImage={isImage}
          isVideo={isVideo}
          isPdf={isPdf}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};

/**
 * Compact attachments display for lists/cards
 */
export const AttachmentsBadge = ({ count, onClick }) => {
  if (!count) return null;

  return (
    <span className="attachments-badge" onClick={onClick} title={`${count} attachment(s)`}>
      📎 {count}
    </span>
  );
};

export default FileAttachments;
